import * as Sentry from "@sentry/browser";
import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";

import AddIcon from "@material-ui/icons/AddOutlined";
import NumberFormat from "react-number-format";
import { useAlert } from "react-alert";
import _ from "lodash";

import { Button, Theme } from "@material-ui/core";

import { useDispatch, useSelector } from "react-redux";

import { useHistory } from "react-router";
import { useParams } from "react-router-dom";

import { useInterval } from "react-use";

import moment from "moment";

import Navigation from "../Navigation.js";
import { makeRequest } from "../util";
import { Cohort } from "./types";
import { CohortEditView } from "./CohortEditView";

import {
  CohortsState,
  REFRESH_COHORT_METADATA,
  SET_COHORTS,
} from "../reducers/cohorts";
import useAccountMetadata from "../hooks/useAccountMetadata.js";
import { CohortsDisabledPage } from "./CohortsDisabledPage";
import { DestinationState } from "../reducers/destinations";
import { LoadedDestinationInfo } from "../types/global";

const useNewCohortStyles = makeStyles((theme: Theme) => ({
  topbar: {
    display: "flex",
    backgroundColor: "transparent",
    width: "100%",
    flex: "0 1 auto",
  },
  icon: {
    display: "flex",
    marginRight: theme.spacing(1),
  },
}));

const NewCohortButton: React.FC<{ onNewCohort: () => void }> = ({
  onNewCohort,
}) => {
  const classes = useNewCohortStyles();

  return (
    <div className={clsx(classes.topbar)}>
      <div className={clsx("flex justify-right items-center h-12")}>
        <Button
          className="h-full py-1"
          color="primary"
          onClick={() => onNewCohort()}
          variant="contained"
        >
          <AddIcon className={classes.icon} />
          Create New Audience
        </Button>
      </div>
    </div>
  );
};

const CohortGridItem: React.FC<{
  cohort: Cohort;
  onClick: (cohort: Cohort) => void;
}> = ({ cohort, onClick }) => {
  const users = cohort.member_count;
  return (
    <div
      className="bg-white py-1 px-3 shadow rounded cursor-pointer hover:shadow-lg"
      onClick={() => onClick(cohort)}
    >
      {users !== null && !cohort.is_pending && (
        <div className="text-purple-700 text-sm">
          <NumberFormat
            value={users}
            thousandSeparator={true}
            displayType={"text"}
            className="inline-block"
          />{" "}
          users
        </div>
      )}
      <div className="text-lg text-gray-700 font-bold line-clamp-2 mb-5">
        {cohort.name}
      </div>
      <div className="text-xs">
        <span className="text-gray-500 mr-1">Destinations:</span>
        <span className="text-gray-700">
          <DestinationSummaryFromCohort cohort={cohort} />
        </span>
      </div>
      <div className="text-xs">
        <span className="text-gray-500 mr-1">Updated:</span>
        <span className="text-gray-700 mr-1">
          {cohort.synced_to
            ? moment(cohort.synced_to).fromNow()
            : "Uninitialized"}
        </span>
        {cohort.is_pending && (
          <span className="text-gray-600">(pending update)</span>
        )}
      </div>
    </div>
  );
};

const DestinationSummaryFromCohort: React.FC<{ cohort: Cohort }> = ({
  cohort,
}) => {
  const destinationDataMap = useSelector(
    (state: { destinations: DestinationState }) => state.destinations.data
  );
  const destinationData = Object.values(destinationDataMap);
  const enabledDestinations = _.sortBy(
    destinationData,
    (dest: LoadedDestinationInfo) => {
      try {
        return dest.name.toLowerCase();
      } catch {
        return dest.name;
      }
    }
  )
    .filter(
      (dest) =>
        cohort.enabled_dests[dest.id] && cohort.enabled_dests[dest.id].enabled
    )
    .map((dest) => dest.name);

  if (enabledDestinations.length === 0) {
    return <span className="text-red-800">no destinations enabled</span>;
  }

  if (enabledDestinations.length === 1) {
    return <span>{enabledDestinations[0]}</span>;
  }

  if (enabledDestinations.length === 2) {
    return (
      <span>
        {enabledDestinations[0]}, {enabledDestinations[1]}
      </span>
    );
  }

  return (
    <span>
      {enabledDestinations[0]}, {enabledDestinations[1]}, and{" "}
      {enabledDestinations.length - 2} more
    </span>
  );
};

const Index: React.FC = () => {
  const accountMetadata = useAccountMetadata();

  if (!accountMetadata || !accountMetadata.config || !accountMetadata.loaded) {
    return null;
  }

  return accountMetadata.config.personas ? (
    <CohortsPage />
  ) : (
    <CohortsDisabledPage />
  );
};

const CohortsPage: React.FC = () => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const history = useHistory();
  const cohorts = useSelector(
    (state: { cohorts: CohortsState }) => state.cohorts.cohorts
  );
  const { cohortId: selectedCohortId } = useParams<{ cohortId: string }>();
  const [isEditingCohort, setIsEditingCohort] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState<Cohort | null>(null);
  const [cohortsLoaded, setCohortsLoaded] = useState<boolean>(false);

  const onSelectCohort = React.useCallback((cohort) => {
    setSelectedCohort(cohort);
    if (cohort) {
      setIsEditingCohort(true);
    } else {
      setIsEditingCohort(false);
    }
  }, []);

  const onCloseEdit = React.useCallback(() => {
    setSelectedCohort(null);
    setIsEditingCohort(false);
    history.replace(`/cohorts/`);
  }, [history]);

  const fetchCohorts = async () => {
    try {
      const response = await makeRequest<any>("get_cohorts", {});
      return response;
    } catch (error) {
      alert.error("Something went wrong. Unable to load page.");
      console.log(error);
      Sentry.captureException(error);
    }
  };

  const initializeCohorts = async () => {
    const response = await fetchCohorts();
    if (response) {
      dispatch({
        type: SET_COHORTS,
        payload: response.data,
      });
      setCohortsLoaded(true);
    }
  };

  const refreshCohortTimes = async () => {
    const response = await fetchCohorts();
    if (response) {
      dispatch({
        type: REFRESH_COHORT_METADATA,
        payload: response.data,
      });
      setCohortsLoaded(true);
    }
  };

  useEffect(() => {
    initializeCohorts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useInterval(refreshCohortTimes, 10000);

  useEffect(() => {
    if (!cohorts || !cohortsLoaded) {
      // Cohorts haven't loaded yet
      return;
    }

    if (selectedCohortId === "new") {
      setIsEditingCohort(true);
      setSelectedCohort(null);
    } else {
      if (selectedCohortId) {
        const selectedCohortByUrl = cohorts.find(
          ({ id }) => id === selectedCohortId
        );
        if (selectedCohortByUrl) {
          history.push(`/cohort/${selectedCohortByUrl.id}/`);
          onSelectCohort(selectedCohortByUrl);
        } else {
          history.push("/cohorts/");
          alert.error("That cohort no longer exists.");
          // Trying to edit an ID that doesn't exist anymore
          onCloseEdit();
        }
      } else {
        onSelectCohort(null);
        history.push("/cohorts/");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohorts, cohortsLoaded, selectedCohortId]);

  if (selectedCohortId && !cohortsLoaded) {
    // The url dictates that a cohort wants to load, but we haven't finished loading the cohorts yet.
    return null;
  }

  return isEditingCohort ? (
    <CohortEditView
      onClose={onCloseEdit}
      selectedCohort={selectedCohort}
      refreshCohortTimes={refreshCohortTimes}
    />
  ) : (
    <CohortSummaryView
      cohorts={cohorts}
      onSelectCohort={onSelectCohort}
      onNewCohort={() => {
        history.replace("/cohort/new");
      }}
    />
  );
};

interface CohortSummaryViewProps {
  cohorts: Cohort[];
  onSelectCohort: (cohort: Cohort) => void;
  onNewCohort: () => void;
}

const CohortSummaryView: React.FC<CohortSummaryViewProps> = ({
  cohorts,
  onSelectCohort,
  onNewCohort,
}) => {
  const history = useHistory();

  return (
    <>
      <NewCohortButton onNewCohort={onNewCohort} />
      <div className="grid grid-cols-4 gap-6 mt-10">
        {cohorts.map((c, index) => (
          <CohortGridItem
            key={index}
            cohort={c}
            onClick={(cohort) => {
              onSelectCohort(cohort);
              history.push(`/cohort/${cohort.id}/`);
            }}
          />
        ))}
      </div>
    </>
  );
};

const CohortPageWithNavigation: React.FC = () => {
  return (
    <Navigation className="pt-6 h-full flex flex-col">
      <Index />
    </Navigation>
  );
};

export default CohortPageWithNavigation;
